import { useState, useEffect } from 'react';
import { getAllAnchors } from "../services/anchorService";
import { fetchWATemplates } from "../services/watemplateService";
import { fetchSubscriptionTemplates } from '../services/subTemplateService';
import { fetchSubscriptions } from "../services/subscriptionService";
import { SingleSelectDropdown } from '../components/SingleSelectDropdown';
import { SubscriptionManager } from './SubscriptionManager';
import { useNavigate } from "react-router-dom";
import { SelectedSubscriptions } from './SelectedSubscriptions';

const DupSubscription = () => {
    const [subscriptions, setSubscriptions] = useState([]);
    const [anchors, setAnchors] = useState([]);
    const [waTemplates, setWATemplates] = useState([]);
    const [planList, setPlanList] = useState([]);
    const [loading, setLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [selectedAnchor, setSelectedAnchor] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedSubs, setSelectedSubs] = useState([]);
    const [selectedPage, setSelectedPage] = useState(1);
    const [bulkAnchor, setBulkAnchor] = useState('');
    const [bulkPlan, setBulkPlan] = useState('');
    const [showConfirm, setShowConfirm] = useState(false);
    const [isCreating, setIsCreating] = useState(false);


    const navigate = useNavigate();


    const itemsPerPage = 4;
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentSubscriptions = subscriptions.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(subscriptions.length / itemsPerPage);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [subs, anchors, templates, plans] = await Promise.all([
                    fetchSubscriptionTemplates(),
                    getAllAnchors(),
                    fetchWATemplates(),
                    fetchSubscriptions()
                ]);
                setSubscriptions(subs);
                setAnchors(anchors);
                setWATemplates(templates);
                setPlanList(plans);
                setCurrentPage(1);
            } catch (error) {
                console.error('Failed to fetch data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleApplyFilters = async () => {
        const filters = {};
        if (selectedAnchor) filters.anchor_id = selectedAnchor;
        if (selectedPlan) filters.subscription_id = selectedPlan;
        if (selectedTemplate) filters.watemplate_id = selectedTemplate;

        try {
            const data = await fetchSubscriptionTemplates(filters);
            setSubscriptions(data);
            setCurrentPage(1);
            setSelectedSubs([]);
        } catch (error) {
            console.error("Failed to apply filters:", error);
        }
    };

    const handleClearFilters = async () => {
        setSelectedAnchor(null);
        setSelectedPlan(null);
        setSelectedTemplate(null);

        try {
            const data = await fetchSubscriptionTemplates();
            setSubscriptions(data);
            setCurrentPage(1);
            setSelectedSubs([]);
        } catch (error) {
            console.error("Failed to clear filters:", error);
        }
    };

    const handleCreateSubscriptions = async () => {
        setIsCreating(true);
        try {
            const results = await SubscriptionManager(selectedSubs);

            const successCount = results.filter(r => r.success).length;
            const failCount = results.length - successCount;

            alert(`Subscriptions Created:\n✅ Success: ${successCount}\n❌ Failed: ${failCount}`);
        } catch (error) {
            alert(`Error: ${error.message || "Something went wrong."}`);
        }
        setIsCreating(false);
        setShowConfirm(false)
        navigate('/dashboard/subscription');
    };


    const formatTimeLabel = (timeStr) => {
        const [hours, minutes] = timeStr.split(":");
        const date = new Date();
        date.setHours(Number(hours), Number(minutes), 0);
        return new Intl.DateTimeFormat("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        }).format(date);
    };

    const isAllSelected = subscriptions.length > 0 && selectedSubs.length === subscriptions.length;

    const toggleSelectAll = () => {
        if (isAllSelected) {
            setSelectedSubs([]);
        } else {
            setSelectedSubs([...subscriptions]);
        }
    };

    const toggleSelectOne = (sub) => {
        setSelectedSubs((prev) => {
            const exists = prev.some((item) => item.id === sub.id);
            if (exists) {
                return prev.filter((item) => item.id !== sub.id);
            } else {
                return [...prev, sub];
            }
        });
    };

    if (loading) return <div className="p-4">Loading...</div>;

    return (
        <div className="p-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-4 my-4 items-end">
                <SingleSelectDropdown
                    label="Anchor"
                    options={anchors}
                    selected={selectedAnchor}
                    setSelected={setSelectedAnchor}
                />
                <SingleSelectDropdown
                    label="Plan"
                    options={planList}
                    selected={selectedPlan}
                    setSelected={setSelectedPlan}
                />
                <SingleSelectDropdown
                    label="WA Template"
                    options={waTemplates}
                    selected={selectedTemplate}
                    setSelected={setSelectedTemplate}
                />

                <button
                    className="bg-blue-600 text-white px-4 py-2 rounded h-fit "
                    onClick={handleApplyFilters}
                >
                    Apply Filters
                </button>

                <button
                    className="bg-gray-400 text-white px-4 py-2 rounded h-fit"
                    onClick={handleClearFilters}
                >
                    Clear
                </button>
            </div>


            <table className="min-w-full table-auto border">
                <thead>
                    <tr className="bg-gray-100 text-left">
                        <th className="px-4 py-2 border">
                            <input
                                type="checkbox"
                                checked={isAllSelected}
                                onChange={toggleSelectAll}
                            /> Select All
                        </th>
                        <th className="px-4 py-2 border">Anchor</th>
                        <th className="px-4 py-2 border">Plan</th>
                        <th className="px-4 py-2 border">WA Template</th>
                        <th className="px-4 py-2 border">Day</th>
                        <th className="px-4 py-2 border">Time</th>
                    </tr>
                </thead>
                <tbody>
                    {currentSubscriptions.map((sub) => (
                        <tr key={sub.id} className="hover:bg-gray-100">
                            <td className="px-4 py-2 border">
                                <input
                                    type="checkbox"
                                    checked={selectedSubs.some((s) => s.id === sub.id)}
                                    onChange={() => toggleSelectOne(sub)}
                                />
                            </td>
                            <td className="px-4 py-2 border">
                                {anchors.find((a) => a.id === sub.anchor)?.name || 'N/A'}
                            </td>
                            <td className="px-4 py-2 border">
                                {planList.find((p) => p.id === sub.subscription)?.name || 'N/A'}
                            </td>
                            <td className="px-4 py-2 border">
                                {waTemplates.find((t) => t.id === sub.watemplate)?.name || 'N/A'}
                            </td>
                            <td className="px-4 py-2 border">{sub.day}</td>
                            <td className="px-4 py-2 border">{formatTimeLabel(sub.time)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-4 flex gap-2 items-center">
                    <button
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                        Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => (
                        <button
                            key={i + 1}
                            onClick={() => setCurrentPage(i + 1)}
                            className={`px-3 py-1 border rounded ${currentPage === i + 1 ? 'bg-blue-600 text-white' : ''}`}
                        >
                            {i + 1}
                        </button>
                    ))}
                    <button
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            )}

            <SelectedSubscriptions
                selectedSubs={selectedSubs}
                setSelectedSubs={setSelectedSubs}
                anchors={anchors}
                planList={planList}
                showConfirm={showConfirm}
                setShowConfirm={setShowConfirm}
                bulkAnchor={bulkAnchor}
                setBulkAnchor={setBulkAnchor}
                bulkPlan={bulkPlan}
                setBulkPlan={setBulkPlan}
                selectedPage={selectedPage}
                setSelectedPage={setSelectedPage}
            />


            {showConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                    <div className="bg-white rounded shadow-lg p-6 w-full max-w-md">
                        <h2 className="text-lg font-bold mb-4">Confirm Action</h2>
                        <p className="mb-4">Are you sure you want to create subscriptions for {selectedSubs.length} items?</p>
                        <div className="flex justify-end gap-4">
                            <button
                                className="px-4 py-2 bg-gray-300 rounded"
                                onClick={() => setShowConfirm(false)}
                                disabled={isCreating}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-4 py-2 bg-green-600 text-white rounded"
                                onClick={handleCreateSubscriptions}
                                disabled={isCreating}
                            >
                                {isCreating ? "Creating..." : "Yes, Create"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default DupSubscription;
